import { ChevronLeft } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BulkPaymentStateModal from "../../Components/BulkPayment/BulkPaymentStateModal";
import CModal from "../../Components/CModal/CModal";
import CToast from "../../Components/CToast/CToast";
import { isLinkPaymentStatusFailed, isLinkPaymentStatusInProgress, LinkPaymentStatus } from "../../Models/LinkPaymentStatus";
import { PaymentType } from "../../Models/PaymentType";
import { ROUTE_PATHS } from "../../Routes/constants";
import { checkBulkAndSinglePaymentLinkStatus } from "../../Services/BankService";
import { getRemoteValue } from "../../Utils/firebase";
import {
    clearPaymentFrameCallbacks,
    getPaymentFrameCallbacks,
} from "../../Utils/paymentFrameCallbacks";


interface PaymentFrameState {
    type: PaymentType;
    url: string;
    token: string;
    referrer?: string; // Optional referrer path to go back to
    /** Serializable handle for callbacks stored in `paymentFrameCallbacks` registry */
    callbackId?: string;
}

const PaymentFramePage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Get the URL and title from navigation state
    const state = location.state as PaymentFrameState;

    const [paymentState, setPaymentState] = useState<LinkPaymentStatus>(LinkPaymentStatus.IN_PROGRESS);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const shouldPollRef = useRef(true);
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const stateRef = useRef(state);
    stateRef.current = state;

    const stopStatusPolling = () => {
        shouldPollRef.current = false;
        if (pollTimeoutRef.current != null) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    };

    const handleModalClose = () => {
        stopStatusPolling();
        navigate("/");
        setIsModalOpen(false);
        clearPaymentFrameCallbacks(stateRef.current?.callbackId);
    };

    const getSuccessMessage = (type: PaymentType) => {
        if (type === PaymentType.ALL || type === PaymentType.LATE || type === PaymentType.CUSTOM) {
            return getRemoteValue("bulk_pay_success_message");
        } else if (type === PaymentType.SINGLE) {
            return getRemoteValue("single_pay_success_message");
        } else if (type === PaymentType.SNOOZE) {
            return getRemoteValue("snooze_success_message");
        } else if (type === PaymentType.BULK_SNOOZE) {
            return getRemoteValue("bulk_snooze_success_message");
        }
    };

    const getFailMessage = (type: PaymentType) => {
        if (type === PaymentType.ALL || type === PaymentType.LATE || type === PaymentType.CUSTOM) {
            return getRemoteValue("bulk_pay_fail_message");
        } else if (type === PaymentType.SINGLE) {
            return getRemoteValue("single_pay_fail_message");
        } else if (type === PaymentType.SNOOZE) {
            return getRemoteValue("snooze_fail_message");
        } else if (type === PaymentType.BULK_SNOOZE) {
            return getRemoteValue("bulk_snooze_fail_message");
        }
    };

    const navigateToReferrer = useCallback(() => {
        stopStatusPolling();
        const backPath = stateRef.current?.referrer || "/";
        navigate(backPath, { replace: true });
    }, [navigate]);

    /**
     * CRL-3241: a successful payment always lands on the payments homepage.
     * The referrer (e.g. /checkout for card payments) is only for going back
     * on failure/abort — returning there after success strands partial
     * (CUSTOM) payments on a stale checkout.
     */
    const navigateToPaymentsHome = useCallback(() => {
        stopStatusPolling();
        navigate(ROUTE_PATHS.DEFAULT_PAYMENTS, { replace: true });
    }, [navigate]);

    const navigateToReferrerAndClearCallbacks = useCallback(() => {
        navigateToReferrer();
        clearPaymentFrameCallbacks(stateRef.current?.callbackId);
    }, [navigateToReferrer]);

    const handleUserGoBack = useCallback(() => {
        navigateToReferrer();
        const id = stateRef.current?.callbackId;
        getPaymentFrameCallbacks(id)?.onBackTriggered?.();
        clearPaymentFrameCallbacks(id);
    }, [navigateToReferrer]);

    useEffect(() => {
        if (!state?.token) {
            return;
        }

        shouldPollRef.current = true;

        const poll = async () => {
            if (!shouldPollRef.current) {
                return;
            }
            const s = stateRef.current;
            if (!s?.token) {
                return;
            }
            try {
                const response = await checkBulkAndSinglePaymentLinkStatus(s.token);
                if (!shouldPollRef.current) {
                    return;
                }
                if (isLinkPaymentStatusInProgress(response!.status)) {
                    pollTimeoutRef.current = setTimeout(() => {
                        void poll();
                    }, 3000);
                } else if (response?.status === LinkPaymentStatus.PAID) {
                    const msg = getSuccessMessage(s.type);
                    if (msg) {
                        CToast(msg, "success");
                    }
                    navigateToPaymentsHome();
                    {
                        const id = stateRef.current?.callbackId;
                        getPaymentFrameCallbacks(id)?.onFinish?.(LinkPaymentStatus.PAID);
                        clearPaymentFrameCallbacks(id);
                    }
                } else {
                    setPaymentState(response!.status);
                    if (isLinkPaymentStatusFailed(response!.status)) {
                        const msg = getFailMessage(s.type);
                        if (msg) {
                            CToast(msg, "error");
                        }
                        navigateToReferrer();
                    } else {
                        setIsModalOpen(true);
                    }
                    const id = stateRef.current?.callbackId;
                    getPaymentFrameCallbacks(id)?.onFinish?.(response?.status ?? LinkPaymentStatus.FAILED);
                }
            } catch (error) {
                console.error("Error checking payment status:", error);
            }
        };

        pollTimeoutRef.current = setTimeout(() => {
            void poll();
        }, 3000);

        return () => {
            stopStatusPolling();
        };
    }, [state?.token, navigate, navigateToReferrer, navigateToPaymentsHome]);

    useEffect(() => {
        // Redirect if no URL is provided in state
        if (!state?.url) {
            navigate(ROUTE_PATHS.DEFAULT_PAYMENTS, { replace: true });
        }
    }, [state, navigate]);


    // Only allow local network access in test/stage environment
    const isTestEnvironment = process.env.REACT_APP_NODE_ENV === "stage";

    // Don't render iframe if no URL is provided
    if (!state?.url) {
        return null;
    }

    return (
        <>
            <div className="md:!px-[5%] lg:!px-[8%]">
                {/* Go Back Button */}
                <Box sx={{ mb: 2, pt: 2 }}>
                    <Button
                        onClick={handleUserGoBack}
                        startIcon={<ChevronLeft />}
                        sx={{
                            color: '#191B1E',
                            textTransform: 'none',
                            fontSize: '16px',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: 'rgba(25, 27, 30, 0.04)',
                            },
                            pl: 1
                        }}
                    >
                        {getRemoteValue("go_back")}
                    </Button>
                </Box>

                {/* Payment Frame */}
                <iframe
                    src={state.url}
                    style={{
                        height: "calc(100vh - 120px)", // Reduced height to account for the button
                        width: "100%",
                        border: "none"
                    }}
                    title={"Odeme"}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    {...(isTestEnvironment && { allow: "local-network-access" })}
                    loading="lazy"
                />

            </div>
            {isModalOpen && (
                <CModal open={isModalOpen} onClose={handleModalClose}>
                    <BulkPaymentStateModal type={state.type} paymentState={paymentState} handleGoBack={navigateToReferrerAndClearCallbacks} />
                </CModal>
            )}
        </>
    );
};

export default PaymentFramePage;
